import { useState, useEffect } from 'react';
import { CheckCircle, ArrowRight, Send, Bell, Sparkles } from 'lucide-react';
import { usePageTitle } from '@hooks/usePageTitle';
import { useProgress } from '@hooks/useProgress';
import ContourBG from '@components/ContourBG';

/* Free tier ships one live skill's worth of exercises today (AI Prompting,
   Phase 1 = 15). When a user has done all of them, that's the honest
   "you've outgrown free" moment — surfaced below, driven by real progress. */
const FREE_TIER_EXERCISES = 15;

/* ─── Per-plan notify form ─── */
const PlanNotifyForm = ({ planName, accent, isDark, c }) => {
  const [email, setEmail]   = useState('');
  const [done, setDone]     = useState(false);
  const [loading, setLoad]  = useState(false);
  const [err, setErr]       = useState('');

  const submit = async e => {
    e.preventDefault();
    if (!email) return;
    setLoad(true); setErr('');
    try {
      const { supabase } = await import('@services/api.js');
      const { error } = await supabase
        .from('waitlist')
        .insert({ email, source: `billing_${planName.toLowerCase()}` });
      if (error) throw error;
      setDone(true);
    } catch (err) {
      console.error('Waitlist error:', err);
      setErr('Something went wrong — try again.');
    } finally {
      setLoad(false);
    }
  };

  if (done) return (
    <div style={{ width:'100%', padding:'10px 0', borderRadius:12, border:'1px solid #22c55e', background: isDark ? 'rgba(34,197,94,0.08)' : '#f0fdf4', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
      <Send size={13} color="#22c55e"/>
      <span style={{ fontSize:13, fontWeight:700, color:'#22c55e' }}>You're on the list!</span>
    </div>
  );

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
      <div style={{ fontSize:11, fontWeight:600, color:c.muted, textAlign:'center' }}>
        Get notified at launch — 20% off
      </div>
      <form onSubmit={submit} style={{ display:'flex', gap:6 }}>
        <input
          type="email" value={email} onChange={e => setEmail(e.target.value)}
          placeholder="your@email.com" required
          style={{ flex:1, minWidth:0, padding:'0 10px', height:38, borderRadius:9, border:`1px solid ${c.cardBorder}`, background:c.inner, color:c.head, fontSize:12, outline:'none', boxSizing:'border-box' }}
        />
        <button
          type="submit" disabled={loading || !email}
          style={{ flexShrink:0, height:38, padding:'0 12px', borderRadius:9, border:'none', background:accent, color:'#fff', fontWeight:700, fontSize:12, cursor: !email || loading ? 'not-allowed' : 'pointer', opacity: !email || loading ? 0.6 : 1, display:'flex', alignItems:'center', gap:5 }}
        >
          {loading
            ? <div style={{ width:12, height:12, border:'2px solid rgba(255,255,255,0.3)', borderTopColor:'#fff', borderRadius:'50%', animation:'spin 0.7s linear infinite' }}/>
            : <><Bell size={12}/> Notify</>}
        </button>
      </form>
      {err && <p style={{ fontSize:11, color:'#ef4444', margin:0 }}>{err}</p>}
    </div>
  );
};

/* ─── Plan definitions — real prices only ─── */
const PLANS = [
  {
    id: 'free',
    name: 'Free',
    monthlyPrice: 0,
    yearlyPrice: 0,
    period: 'forever',
    tagline: 'Build the foundation. No card needed.',
    features: [
      'AI Prompt Engineering — Phase 1',
      '15 scored exercises',
      'Real AI scoring (0–100)',
      'Progress tracking & streaks',
    ],
    accent: '#ea580c',
    current: true,
  },
  {
    id: 'explorer',
    name: 'Explorer',
    monthlyPrice: 15,
    yearlyPrice: 150,
    tagline: 'Build multiple skills at your own pace.',
    features: [
      'Choose any 3 skills',
      'All phases & lessons',
      'AI feedback & scoring',
      'Priority support',
    ],
    accent: '#7c3aed',
    badge: 'Most Popular',
    comingSoon: true,
  },
  {
    id: 'pro',
    name: 'Pro',
    monthlyPrice: 50,
    yearlyPrice: 500,
    tagline: 'Full access. No limits.',
    features: [
      'All skills unlocked',
      'Voice Room — unlimited',
      'Early access to new skills',
      'Priority support',
    ],
    accent: '#0ea5e9',
    badge: 'Best Value',
    comingSoon: true,
  },
];

/* ─── Billing page ─── */
const Billing = () => {
  usePageTitle('Plans & Billing — Castle Atlas');

  const [billing, setBilling] = useState('monthly');
  const { totalCompleted = 0 } = useProgress();

  const [isDark, setIsDark] = useState(() =>
    typeof window !== 'undefined' && document.documentElement.classList.contains('dark')
  );
  useEffect(() => {
    const obs = new MutationObserver(() =>
      setIsDark(document.documentElement.classList.contains('dark'))
    );
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  const c = {
    card:        isDark ? '#1c1917' : '#ffffff',
    cardBorder:  isDark ? '#2d2825' : '#e8e4df',
    inner:       isDark ? '#151211' : '#f5f2ee',
    innerBorder: isDark ? '#2d2825' : '#e8e4df',
    head:        isDark ? '#fafaf9' : '#1c1917',
    muted:       isDark ? '#78716c' : '#a8a29e',
    sub:         isDark ? '#57534e' : '#c4bcb6',
    divider:     isDark ? '#2d2825' : '#f0ece7',
    featureText: isDark ? '#d4cdc7' : '#57534e',
  };

  const headColor = c.head;
  const mutedColor = c.muted;
  const dashColor = isDark ? '#57534e' : '#d4cdc7';
  const pageBg = isDark ? '#0e0d0c' : '#faf9f7';

  /* Free-ceiling framing: how close is this user to exhausting free? */
  const freeProgress = Math.min(totalCompleted / FREE_TIER_EXERCISES, 1);
  const hitCeiling = totalCompleted >= FREE_TIER_EXERCISES;
  const nearingCeiling = !hitCeiling && freeProgress >= 0.6;
  const showCeilingBanner = hitCeiling || nearingCeiling;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: pageBg }}>
      <style>{`
        @keyframes se { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        .se1{animation:se 0.3s ease-out both}
        .se2{animation:se 0.3s ease-out 0.07s both}
        .se3{animation:se 0.3s ease-out 0.14s both}
        .se4{animation:se 0.3s ease-out 0.21s both}
        .plan-card{transition:transform 0.2s ease,box-shadow 0.2s ease}
        .plan-card:hover{transform:translateY(-3px);box-shadow:0 16px 40px -8px rgba(0,0,0,0.18)}
        @keyframes spin{to{transform:rotate(360deg)}}
        .plans-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:16px;margin-bottom:32px}
        @media(max-width:860px){.plans-grid{grid-template-columns:1fr;max-width:440px}}
        @media(max-width:500px){.plan-price-num{font-size:32px!important}}
        @media(prefers-reduced-motion:reduce){.se1,.se2,.se3,.se4{animation:none!important}}
      `}</style>

      {/* ── Header — Atlas Plates ── */}
      <div style={{ position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 150, overflow: 'hidden', pointerEvents: 'none' }}>
          <ContourBG isDark={isDark} size="compact" opacity={0.9} />
        </div>
        <div className="se1 rail-clearance" style={{ maxWidth: '100%', padding: '48px 24px 20px', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: '#ea580c', letterSpacing: '0.14em' }}>THE PASS</span>
            <span style={{ width: 22, height: 1, background: dashColor, display: 'inline-block' }} />
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: mutedColor, letterSpacing: '0.14em', textTransform: 'uppercase' }}>Plans &amp; Billing</span>
          </div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.7rem,4.5vw,2.2rem)', fontWeight: 900, letterSpacing: '-0.03em', color: headColor, margin: '0 0 8px', lineHeight: 1.1 }}>
            Plans &amp; Pricing
          </h1>
          <p style={{ fontSize: 14, color: mutedColor, maxWidth: 480, margin: 0, lineHeight: 1.6 }}>
            You're on the Free plan. Upgrade anytime when paid plans go live.
          </p>
        </div>
      </div>

      <div className="rail-clearance" style={{ maxWidth: '100%', padding: '20px 24px 72px' }}>

        {/* ── Free-ceiling banner — real progress, the honest conversion moment ── */}
        {showCeilingBanner && (
          <div className="se2" style={{
            marginBottom: 28, padding: '18px 22px', borderRadius: 16,
            background: isDark ? 'linear-gradient(145deg,#1a0f08,#21130a)' : 'linear-gradient(145deg,#fff7ed,#ffedd5)',
            border: `1.5px solid ${isDark ? '#5c3d1e' : '#fdba74'}`,
            display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap',
          }}>
            <div style={{ flex: 1, minWidth: 240 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
                <Sparkles size={15} color="#ea580c" />
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 800, color: '#ea580c', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                  {hitCeiling ? "You've cleared the free tier" : 'Almost through the free tier'}
                </span>
              </div>
              <p style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 900, color: headColor, margin: '0 0 4px', lineHeight: 1.2 }}>
                {hitCeiling
                  ? `${totalCompleted} exercises done — you've outgrown Free`
                  : `${totalCompleted} of ${FREE_TIER_EXERCISES} free exercises done`}
              </p>
              <p style={{ fontSize: 13, color: mutedColor, margin: 0, lineHeight: 1.5 }}>
                {hitCeiling
                  ? 'Explorer unlocks 4 more skills and every phase. Join the launch list below for 20% off.'
                  : "When you finish, Explorer opens up 4 more skills. Here's what's next."}
              </p>
              {/* progress bar */}
              <div style={{ marginTop: 12, height: 6, borderRadius: 99, background: isDark ? 'rgba(0,0,0,0.25)' : 'rgba(234,88,12,0.12)', overflow: 'hidden', maxWidth: 320 }}>
                <div style={{ height: '100%', width: `${Math.round(freeProgress * 100)}%`, background: '#ea580c', borderRadius: 99, transition: 'width 0.7s cubic-bezier(0.22,1,0.36,1)' }} />
              </div>
            </div>
          </div>
        )}

        {/* Monthly / Yearly toggle */}
        <div className="se2" style={{ display:'flex', alignItems:'center', gap:12, marginBottom:28, flexWrap:'wrap' }}>
          <div style={{ position:'relative', display:'inline-flex', background:c.inner, borderRadius:12, padding:4, border:`1px solid ${c.cardBorder}` }}>
            <div style={{
              position:'absolute',
              left: billing === 'monthly' ? 4 : 'calc(50% + 2px)',
              width:'calc(50% - 6px)', top:4, bottom:4,
              background:c.card, borderRadius:9,
              transition:'left 0.25s cubic-bezier(0.4,0,0.2,1)',
              boxShadow:'0 1px 4px rgba(0,0,0,0.12)',
            }}/>
            {[{val:'monthly',label:'Monthly'},{val:'yearly',label:'Yearly'}].map(({val,label}) => (
              <button key={val} onClick={() => setBilling(val)}
                style={{ position:'relative', zIndex:1, padding:'7px 22px', fontSize:12, fontWeight:700, border:'none', background:'transparent', cursor:'pointer', color: billing===val ? c.head : c.muted, transition:'color 0.2s' }}>
                {label}
              </button>
            ))}
          </div>
          <span style={{
            fontSize:11, fontWeight:700,
            color: billing==='yearly' ? '#22c55e' : c.muted,
            background: billing==='yearly' ? (isDark ? 'rgba(34,197,94,0.1)' : '#f0fdf4') : c.inner,
            padding:'5px 12px', borderRadius:99,
            border:`1px solid ${billing==='yearly' ? (isDark ? 'rgba(34,197,94,0.2)' : '#bbf7d0') : c.innerBorder}`,
            transition:'all 0.2s',
          }}>
            {billing==='yearly' ? '🎉 2 months free with yearly' : 'Save with yearly billing'}
          </span>
        </div>

        {/* Plan cards */}
        <div className="se3 plans-grid">
          {PLANS.map(plan => {
            const isYearly = billing === 'yearly';
            const isFree   = plan.id === 'free';
            const price    = isYearly ? plan.yearlyPrice : plan.monthlyPrice;
            const saving   = plan.monthlyPrice > 0 ? (plan.monthlyPrice * 12) - plan.yearlyPrice : 0;

            return (
              <div key={plan.id} className="plan-card" style={{
                position:'relative',
                background: plan.current ? (isDark ? 'rgba(234,88,12,0.07)' : '#fff7ed') : c.card,
                border:`2px solid ${plan.current ? '#ea580c' : c.cardBorder}`,
                borderRadius:20, padding:24,
                display:'flex', flexDirection:'column',
              }}>
                {plan.badge && (
                  <div style={{ position:'absolute', top:-12, left:18 }}>
                    <span style={{ background:plan.accent, color:'#fff', fontSize:10, fontWeight:800, padding:'3px 10px', borderRadius:99 }}>
                      {plan.badge}
                    </span>
                  </div>
                )}

                {/* Name + status */}
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4, flexWrap:'wrap' }}>
                  <span style={{ fontFamily:'var(--font-display)', fontSize:18, fontWeight:900, color:c.head }}>{plan.name}</span>
                  {plan.current && (
                    <span style={{ fontSize:10, fontWeight:700, color:'#22c55e', background: isDark ? 'rgba(34,197,94,0.12)' : '#f0fdf4', padding:'2px 8px', borderRadius:99, border:`1px solid ${isDark ? 'rgba(34,197,94,0.25)' : '#bbf7d0'}` }}>
                      ACTIVE
                    </span>
                  )}
                  {plan.comingSoon && (
                    <span style={{ fontSize:10, fontWeight:700, color:c.muted, background:c.inner, padding:'2px 8px', borderRadius:99 }}>
                      SOON
                    </span>
                  )}
                </div>

                <p style={{ fontSize:12, color:c.muted, marginBottom:16, lineHeight:1.4 }}>{plan.tagline}</p>

                {/* Price — Free shows "always free" statically so the toggle
                    clearly only affects paid tiers (was showing $0 either way,
                    making the toggle look broken on this card) */}
                <div style={{ marginBottom:20 }}>
                  {isFree ? (
                    <div style={{ display:'flex', alignItems:'flex-end', gap:6, marginBottom:6 }}>
                      <span className="plan-price-num" style={{ fontSize:40, fontWeight:900, color:c.head, letterSpacing:'-0.04em', lineHeight:1 }}>Free</span>
                      <span style={{ fontSize:12, color:c.muted, marginBottom:8 }}>always</span>
                    </div>
                  ) : (
                    <div style={{ display:'flex', alignItems:'flex-end', gap:3, marginBottom:6 }}>
                      <span style={{ fontSize:13, color:c.muted, marginBottom:7 }}>$</span>
                      <span className="plan-price-num" style={{ fontSize:40, fontWeight:900, color:c.head, letterSpacing:'-0.04em', lineHeight:1 }}>{price}</span>
                      <span style={{ fontSize:12, color:c.muted, marginBottom:8 }}>
                        {isYearly ? '/year' : '/month'}
                      </span>
                    </div>
                  )}
                  <div style={{ height:18 }}>
                    {isYearly && !isFree && saving > 0 && (
                      <span style={{ fontSize:12, color:'#22c55e', fontWeight:700 }}>Save ${saving}/year vs monthly</span>
                    )}
                  </div>
                </div>

                <div style={{ height:1, background:c.divider, marginBottom:16 }}/>

                {/* Features */}
                <ul style={{ flex:1, display:'flex', flexDirection:'column', gap:10, marginBottom:20, listStyle:'none', padding:0, margin:'0 0 20px' }}>
                  {plan.features.map(f => (
                    <li key={f} style={{ display:'flex', alignItems:'flex-start', gap:8 }}>
                      <CheckCircle size={13} color={plan.comingSoon && !plan.current ? c.sub : plan.accent} style={{ flexShrink:0, marginTop:2 }}/>
                      <span style={{ fontSize:13, color: plan.comingSoon && !plan.current ? c.muted : c.featureText, lineHeight:1.4 }}>{f}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                {plan.current ? (
                  <div style={{ width:'100%', padding:'12px 0', borderRadius:12, background:c.inner, fontWeight:700, fontSize:13, color:c.muted, display:'flex', alignItems:'center', justifyContent:'center' }}>
                    ✓ Current Plan
                  </div>
                ) : plan.comingSoon ? (
                  <PlanNotifyForm planName={plan.name} accent={plan.accent} isDark={isDark} c={c}/>
                ) : (
                  <button style={{ width:'100%', padding:'12px 0', borderRadius:12, border:'none', fontWeight:700, fontSize:13, cursor:'pointer', background:plan.accent, color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
                    Upgrade to {plan.name} <ArrowRight size={13}/>
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer trust — single consolidated disclaimer (was three separate ones) */}
        <div className="se4" style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:10 }}>
          <div style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'8px 16px', borderRadius:99, background:c.inner, border:`1px solid ${c.cardBorder}` }}>
            <div style={{ width:8, height:8, borderRadius:'50%', background:'#635BFF' }}/>
            <span style={{ fontSize:12, fontWeight:700, color:'#635BFF' }}>Payments secured by Stripe</span>
          </div>
          <p style={{ fontSize:12, color:c.muted, textAlign:'center', maxWidth:360, lineHeight:1.6 }}>
            Paid plans launch soon — join the list for 20% off. Phase 1 is free forever.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Billing;
